import React, { useState } from 'react';
import { X, Camera, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { toast } from '../V2Components';
import PaintService from '../../../services/PaintService';
import { useFilePicker } from '../../../hooks/useFilePicker';

const SHEEN_OPTIONS = ['Flat', 'Matte', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss'];

// Empty colorant row used when the user clicks "Add colorant" while editing
// the tint formula manually. The UI stores rows as ordered arrays so users
// can reorder freely; we serialize to the canonical {label: value} dict on save.
const EMPTY_COLORANT_ROW = { label: '', value: '' };

const EMPTY_FORM = {
    color_name: '',
    manufacturer: '',
    sheen: '',
    product_line: '',
    color_code: '',
    product_code: '',
    base: '',
    base_code: '',
    purchase_date: '',
    purchase_store: '',
    order_number: '',
    can_size: '',
    barcode: '',
    notes: '',
    // tint_formula is stored as flat dict in the API; we keep an ordered
    // array of {label, value} rows during editing for stable React keys.
    tint_formula_system: '',
    tint_formula_can_size: '',
    tint_formula_rows: [],
    tint_formula_raw_text: '',
};

/**
 * Converts API tint_formula object → ordered editing array.
 * Returns [] when formula is null/undefined or has no colorants.
 */
const tintFormulaToRows = (formula) => {
    if (!formula || !formula.colorants) return [];
    return Object.entries(formula.colorants).map(([label, value]) => ({ label, value }));
};

/**
 * Converts ordered editing array → canonical {label: value} dict.
 * Drops rows where label is empty (treat as deleted). Last-write-wins on
 * duplicate labels — UI should ideally prevent duplicates but we don't
 * fight the user about it.
 */
const rowsToColorants = (rows) => {
    const out = {};
    rows.forEach(({ label, value }) => {
        const trimmedLabel = (label || '').trim();
        if (trimmedLabel) out[trimmedLabel] = value || '';
    });
    return out;
};

/**
 * Modal for creating or editing a shared Paint in the user's library.
 *
 * Edits made here affect EVERY room that references the paint — that's the
 * point of having a shared library. The two-pencil pattern in RoomDetails
 * lets users choose between "edit the shared paint" (this modal) and
 * "edit how it's applied here" (RoomPaintModal).
 *
 * Props:
 *   isOpen    - boolean
 *   onClose   - () => void
 *   onSave    - (paintData) => Promise<void>  — parent owns the API call
 *   paint     - existing Paint object for edit mode, null for create
 *   saving    - boolean — disables submit while parent's API call is in flight
 */
const PaintFormModal = ({ isOpen, onClose, onSave, paint, saving }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showFormula, setShowFormula] = useState(false);
    const [scanning, setScanning] = useState(false);

    // OS-native file chooser for the Scan label button. See hooks/useFilePicker
    // for why this matters on mobile (tl;dr: gives iOS users the native
    // "Take Photo / Photo Library / Choose File" sheet instead of dumping
    // them straight into a file browser).
    const { triggerPicker: openScanPicker, fileInputElement: scanFileInput } = useFilePicker({
        kind: 'image',
        onFiles: (files) => handleScanLabel(files[0]),
        disabled: scanning || saving,
    });

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later, losing any input that lands in
    // between (see CircuitForm). Setting state while rendering makes React
    // re-render before committing, so stale values never reach the DOM.
    const openKey = isOpen ? `${paint?.id ?? 'new'}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            if (paint) {
                setFormData({
                    color_name: paint.color_name || '',
                    manufacturer: paint.manufacturer || '',
                    sheen: paint.sheen || '',
                    product_line: paint.product_line || '',
                    color_code: paint.color_code || '',
                    product_code: paint.product_code || '',
                    base: paint.base || '',
                    base_code: paint.base_code || '',
                    purchase_date: paint.purchase_date ? paint.purchase_date.split('T')[0] : '',
                    purchase_store: paint.purchase_store || '',
                    order_number: paint.order_number || '',
                    can_size: paint.can_size || '',
                    barcode: paint.barcode || '',
                    notes: paint.notes || '',
                    tint_formula_system: paint.tint_formula?.system || '',
                    tint_formula_can_size: paint.tint_formula?.can_size || '',
                    tint_formula_rows: tintFormulaToRows(paint.tint_formula),
                    tint_formula_raw_text: paint.tint_formula_raw_text || '',
                });
                // Auto-expand the formula section when editing if there's data to see.
                setShowFormula(
                    (paint.tint_formula?.colorants && Object.keys(paint.tint_formula.colorants).length > 0)
                    || !!paint.tint_formula_raw_text
                );
            } else {
                setFormData(EMPTY_FORM);
                setShowFormula(false);
            }
            setShowAdvanced(false);
        }
    }

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleColorantChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: prev.tint_formula_rows.map(
                (row, i) => i === index ? { ...row, [field]: value } : row
            ),
        }));
    };

    const handleAddColorant = () => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: [...prev.tint_formula_rows, { ...EMPTY_COLORANT_ROW }],
        }));
    };

    const handleRemoveColorant = (index) => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: prev.tint_formula_rows.filter((_, i) => i !== index),
        }));
    };

    // Scan a label photo and prefill the form. Falls back to a user-visible toast
    // on failure rather than silently dropping. Doesn't overwrite fields the user
    // already filled in — scan supplements, doesn't clobber.
    // Receives a single File; the useFilePicker hook handles the input-value
    // reset so the same file can be re-selected.
    const handleScanLabel = async (file) => {
        if (!file) return;

        setScanning(true);
        try {
            const result = await PaintService.scanPaintLabel(file);
            if (!result.success || !result.paint) {
                toast.error(result.error || 'Scan failed. Try again with a clearer photo.');
                return;
            }
            const scanned = result.paint;
            setFormData(prev => ({
                ...prev,
                color_name: prev.color_name || scanned.color_name || '',
                manufacturer: prev.manufacturer || scanned.manufacturer || '',
                sheen: prev.sheen || scanned.sheen || '',
                product_line: prev.product_line || scanned.product_line || '',
                color_code: prev.color_code || scanned.color_code || '',
                product_code: prev.product_code || scanned.product_code || '',
                base: prev.base || scanned.base || '',
                base_code: prev.base_code || scanned.base_code || '',
                purchase_date: prev.purchase_date || (scanned.purchase_date || '').split('T')[0],
                purchase_store: prev.purchase_store || scanned.purchase_store || '',
                order_number: prev.order_number || scanned.order_number || '',
                can_size: prev.can_size || scanned.can_size || '',
                barcode: prev.barcode || scanned.barcode || '',
                tint_formula_system: prev.tint_formula_system || scanned.tint_formula?.system || '',
                tint_formula_can_size: prev.tint_formula_can_size || scanned.tint_formula?.can_size || '',
                tint_formula_rows: prev.tint_formula_rows.length > 0
                    ? prev.tint_formula_rows
                    : tintFormulaToRows(scanned.tint_formula),
                tint_formula_raw_text: prev.tint_formula_raw_text || scanned.tint_formula_raw_text || '',
            }));
            // Expand both sections so the user can see what the scan filled in.
            setShowAdvanced(true);
            if (scanned.tint_formula?.colorants && Object.keys(scanned.tint_formula.colorants).length > 0) {
                setShowFormula(true);
            }
            toast.success('Label scanned. Review the fields before saving.');
        } catch (err) {
            // Branch on HTTP status to show meaningful messages. The backend
            // maps scanner failure codes to status: 400 = user-fixable input
            // problem (HEIC decode failure, unsupported file); 429 = quota;
            // 502 = upstream Gemini failure (timeout, truncation, parse error).
            // For 502 specifically, the backend's `error` field already says
            // "try a clearer photo" — surface that verbatim so the user gets
            // actionable guidance instead of a generic "scan failed" toast.
            let msg;
            if (err.code === 429) {
                msg = 'Monthly scan limit reached.';
            } else if (err.code === 502) {
                msg = err.message || "Couldn't read this label. Try a clearer photo.";
            } else if (err.code === 400) {
                msg = err.message || 'This file type isn\'t supported. Try a JPEG or PNG.';
            } else {
                msg = err.message || 'Scan failed. Please try again.';
            }
            toast.error(msg);
        } finally {
            setScanning(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Mirror the backend CHECK constraint: at least one of color_name / color_code.
        // The backend would 400 either way, but failing fast in the UI is nicer.
        if (!formData.color_name.trim() && !formData.color_code.trim()) {
            toast.error('Paint needs at least a color name or a color code.');
            return;
        }

        // Build the canonical payload. Empty strings → null so the API doesn't
        // store empty strings that compare differently from missing data.
        const colorants = rowsToColorants(formData.tint_formula_rows);
        const hasFormula =
            Object.keys(colorants).length > 0
            || formData.tint_formula_system.trim()
            || formData.tint_formula_can_size.trim();

        const payload = {
            color_name: formData.color_name.trim() || null,
            manufacturer: formData.manufacturer.trim() || null,
            sheen: formData.sheen || null,
            product_line: formData.product_line.trim() || null,
            color_code: formData.color_code.trim() || null,
            product_code: formData.product_code.trim() || null,
            base: formData.base.trim() || null,
            base_code: formData.base_code.trim() || null,
            purchase_date: formData.purchase_date || null,
            purchase_store: formData.purchase_store.trim() || null,
            order_number: formData.order_number.trim() || null,
            can_size: formData.can_size.trim() || null,
            barcode: formData.barcode.trim() || null,
            notes: formData.notes.trim() || null,
            tint_formula: hasFormula
                ? {
                    system: formData.tint_formula_system.trim() || null,
                    can_size: formData.tint_formula_can_size.trim() || null,
                    colorants,
                }
                : null,
            tint_formula_raw_text: formData.tint_formula_raw_text.trim() || null,
        };

        onSave(payload);
    };

    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="paint-form-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
            <div className="bg-[var(--bg-card)] w-full max-w-2xl rounded-2xl shadow-xl p-6 relative theme-transition max-h-[90vh] overflow-y-auto">
                <button
                    type="button"
                    aria-label="Close dialog"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-main)] theme-transition"
                >
                    <X size={20} />
                </button>

                <div className="flex items-center justify-between mb-6 pr-8">
                    <h3 id="paint-form-modal-title" className="text-xl font-bold text-[var(--text-main)] theme-transition">
                        {paint ? 'Edit Paint' : 'Add Paint'}
                    </h3>
                    {/* Scan Label button — disabled while a scan is in flight or while the parent's save is in flight */}
                    <button
                        type="button"
                        onClick={openScanPicker}
                        disabled={scanning || saving}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-card-hover)] text-[var(--text-main)] rounded-lg border border-[var(--border)] disabled:opacity-50 theme-transition"
                    >
                        <Camera size={16} />
                        {scanning ? 'Scanning...' : 'Scan label'}
                    </button>
                    {scanFileInput}
                </div>

                {/* The "shared paint" badge appears in edit mode to make the
                    write-everywhere-effect explicit. New paints are unattached so the
                    warning would be confusing. */}
                {paint && (
                    <div className="mb-4 px-3 py-2 rounded-lg bg-[var(--bg-hover)] text-xs text-[var(--text-secondary)] theme-transition">
                        Editing this paint changes it everywhere it&apos;s used. To change just one room&apos;s surface or
                        date, close this and use &quot;Edit how it&apos;s applied here&quot; instead.
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* --- Identity (always visible) --- */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>
                                Color Name
                            </label>
                            <input
                                value={formData.color_name}
                                onChange={(e) => handleChange('color_name', e.target.value)}
                                placeholder="e.g. Manual Opaline"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>Manufacturer</label>
                            <input
                                value={formData.manufacturer}
                                onChange={(e) => handleChange('manufacturer', e.target.value)}
                                placeholder="e.g. Sherwin-Williams"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Sheen / Finish</label>
                            <select
                                value={formData.sheen}
                                onChange={(e) => handleChange('sheen', e.target.value)}
                                className={inputClass}
                            >
                                <option value="">Select sheen...</option>
                                {SHEEN_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Color Code</label>
                            <input
                                value={formData.color_code}
                                onChange={(e) => handleChange('color_code', e.target.value)}
                                placeholder="e.g. IFC 8112NP"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    {/* --- Advanced section (collapsed by default) --- */}
                    <button
                        type="button"
                        onClick={() => setShowAdvanced(v => !v)}
                        className="flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-main)] theme-transition"
                    >
                        {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        Advanced details
                    </button>

                    {showAdvanced && (
                        <div className="space-y-4 pl-6 border-l-2 border-[var(--border)] theme-transition">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Product Line</label>
                                    <input
                                        value={formData.product_line}
                                        onChange={(e) => handleChange('product_line', e.target.value)}
                                        placeholder="e.g. Emerald Interior Flat"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Product Code (SKU)</label>
                                    <input
                                        value={formData.product_code}
                                        onChange={(e) => handleChange('product_code', e.target.value)}
                                        placeholder="e.g. K35W00351"
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Base</label>
                                    <input
                                        value={formData.base}
                                        onChange={(e) => handleChange('base', e.target.value)}
                                        placeholder="e.g. Extra White"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Base Code</label>
                                    <input
                                        value={formData.base_code}
                                        onChange={(e) => handleChange('base_code', e.target.value)}
                                        placeholder="e.g. 650864309"
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Can Size</label>
                                    <input
                                        value={formData.can_size}
                                        onChange={(e) => handleChange('can_size', e.target.value)}
                                        placeholder="e.g. GALLON"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Purchase Date</label>
                                    <input
                                        type="date"
                                        value={formData.purchase_date}
                                        onChange={(e) => handleChange('purchase_date', e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Purchase Store</label>
                                    <input
                                        value={formData.purchase_store}
                                        onChange={(e) => handleChange('purchase_store', e.target.value)}
                                        placeholder="e.g. SW Store 8035"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Order Number</label>
                                    <input
                                        value={formData.order_number}
                                        onChange={(e) => handleChange('order_number', e.target.value)}
                                        placeholder="e.g. 0132840"
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Barcode</label>
                                <input
                                    value={formData.barcode}
                                    onChange={(e) => handleChange('barcode', e.target.value)}
                                    placeholder="e.g. 0132840-001"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    )}

                    {/* --- Tint formula section (also collapsed by default) --- */}
                    <button
                        type="button"
                        onClick={() => setShowFormula(v => !v)}
                        className="flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-main)] theme-transition"
                    >
                        {showFormula ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        Tint formula
                        {formData.tint_formula_rows.length > 0 && (
                            <span className="text-xs text-[var(--text-muted)]">
                                ({formData.tint_formula_rows.length} colorant{formData.tint_formula_rows.length === 1 ? '' : 's'})
                            </span>
                        )}
                    </button>

                    {showFormula && (
                        <div className="space-y-3 pl-6 border-l-2 border-[var(--border)] theme-transition">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>System</label>
                                    <input
                                        value={formData.tint_formula_system}
                                        onChange={(e) => handleChange('tint_formula_system', e.target.value)}
                                        placeholder="e.g. CCE, CCA"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Formula Can Size</label>
                                    <input
                                        value={formData.tint_formula_can_size}
                                        onChange={(e) => handleChange('tint_formula_can_size', e.target.value)}
                                        placeholder="e.g. GALLON"
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            {/* Colorant rows. Each manufacturer uses a different amount
                                notation (SW comma-list, KM parts, Behr/PPG 384ths). We
                                store both fields as raw strings since the units aren't
                                interoperable across manufacturers. */}
                            <div className="space-y-2">
                                <div className="text-xs font-semibold text-[var(--text-secondary)] uppercase theme-transition">
                                    Colorants
                                </div>
                                {formData.tint_formula_rows.length === 0 && (
                                    <p className="text-xs text-[var(--text-muted)] italic">
                                        No colorants yet. Click &quot;Add colorant&quot; or paste raw OCR text below.
                                    </p>
                                )}
                                {formData.tint_formula_rows.map((row, i) => (
                                    <div key={i} className="flex gap-2">
                                        <input
                                            value={row.label}
                                            onChange={(e) => handleColorantChange(i, 'label', e.target.value)}
                                            placeholder="Code, e.g. B1-Black"
                                            className={`${inputClass} flex-1`}
                                        />
                                        <input
                                            value={row.value}
                                            onChange={(e) => handleColorantChange(i, 'value', e.target.value)}
                                            placeholder="Amount, e.g. 0 OZ, 3/32, 1/128"
                                            className={`${inputClass} flex-1 font-mono text-sm`}
                                        />
                                        <button
                                            type="button"
                                            aria-label={`Remove colorant ${row.label || i + 1}`}
                                            onClick={() => handleRemoveColorant(i)}
                                            className="px-3 text-[var(--text-muted)] hover:text-red-500 theme-transition"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={handleAddColorant}
                                    className="flex items-center gap-2 text-sm text-[var(--primary)] hover:opacity-80"
                                >
                                    <Plus size={16} />
                                    Add colorant
                                </button>
                            </div>

                            <div>
                                <label className={labelClass}>Raw OCR text (fallback)</label>
                                <textarea
                                    value={formData.tint_formula_raw_text}
                                    onChange={(e) => handleChange('tint_formula_raw_text', e.target.value)}
                                    placeholder="When the colorant table is too noisy to parse, paste the verbatim OCR text here."
                                    rows={3}
                                    className={`${inputClass} font-mono text-sm resize-none`}
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className={labelClass}>Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            placeholder="Any additional notes..."
                            rows={2}
                            className={`${inputClass} resize-none`}
                        />
                    </div>

                    <div className="flex space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || scanning}
                            className="flex-1 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (paint ? 'Update' : 'Add')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PaintFormModal;
